/**
 * Skip Navigation Component
 * 
 * Provides keyboard users with the ability to skip directly to main content,
 * improving accessibility and navigation efficiency for screen reader users.
 */

import React from 'react'
import { useI18n } from '../../hooks/useI18n'

interface SkipNavigationProps {
  /**
   * Target IDs for skip links
   */
  targets?: {
    mainContent: string
    navigation: string
    footer: string
    search?: string
  }
  
  /**
   * Custom skip links
   */
  customLinks?: Array<{
    href: string
    label: string
    priority: 'high' | 'medium' | 'low'
  }>
  
  /**
   * Position of skip navigation
   */
  position?: 'top-left' | 'top-center' | 'top-right'
  
  /**
   * Visual style variant
   */
  variant?: 'default' | 'minimal' | 'prominent'
  
  /**
   * Enable keyboard shortcuts for skip navigation
   */
  enableShortcuts?: boolean
}

export const SkipNavigation: React.FC<SkipNavigationProps> = ({
  targets = {
    mainContent: 'main-content',
    navigation: 'main-navigation',
    footer: 'page-footer'
  },
  customLinks = [],
  position = 'top-left',
  variant = 'default',
  enableShortcuts: _enableShortcuts = false
}) => {
  const { navigation: nav } = useI18n()

  // Default skip links
  const defaultSkipLinks = [
    {
      href: `#${targets.mainContent}`,
      label: nav('skip_navigation.main_content'),
      priority: 'high' as const
    },
    {
      href: `#${targets.navigation}`,
      label: nav('skip_navigation.navigation'),
      priority: 'medium' as const
    },
    ...(targets.search ? [{
      href: `#${targets.search}`,
      label: 'Skip to search',
      priority: 'medium' as const
    }] : []),
    {
      href: `#${targets.footer}`,
      label: nav('skip_navigation.footer'),
      priority: 'low' as const
    }
  ]

  // Combine default and custom links, sort by priority
  const allLinks = [...defaultSkipLinks, ...customLinks].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 }
    return priorityOrder[a.priority] - priorityOrder[b.priority]
  })

  // Handle skip link click
  const handleSkipClick = (event: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    event.preventDefault()
    
    const targetElement = document.querySelector(targetId)
    if (targetElement) {
      // Set focus to the target element
      const focusableElement = targetElement as HTMLElement
      
      // If the element isn't naturally focusable, make it temporarily focusable
      const originalTabIndex = focusableElement.getAttribute('tabindex')
      if (!focusableElement.matches(':focus-visible')) {
        focusableElement.setAttribute('tabindex', '-1')
      }
      
      // Smooth scroll to element
      focusableElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      })
      
      // Focus the element
      focusableElement.focus()
      
      // Restore original tabindex after focus
      setTimeout(() => {
        if (originalTabIndex !== null) {
          focusableElement.setAttribute('tabindex', originalTabIndex)
        } else if (focusableElement.getAttribute('tabindex') === '-1') {
          focusableElement.removeAttribute('tabindex')
        }
      }, 100)

      // Announce to screen readers
      const announcement = document.createElement('div')
      announcement.setAttribute('aria-live', 'polite')
      announcement.setAttribute('aria-atomic', 'true')
      announcement.className = 'sr-only'
      announcement.textContent = `Skipped to ${event.currentTarget.textContent}`
      document.body.appendChild(announcement)
      
      setTimeout(() => {
        document.body.removeChild(announcement)
      }, 1000)
    }
  }

  // Get position classes
  const getPositionClasses = () => {
    switch (position) {
      case 'top-center':
        return 'left-1/2 transform -translate-x-1/2'
      case 'top-right':
        return 'right-4'
      default:
        return 'left-4'
    }
  }

  // Get variant classes
  const getVariantClasses = () => {
    const base = 'inline-block px-4 py-2 rounded-md font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
    
    switch (variant) {
      case 'minimal':
        return `${base} bg-gray-800 text-white text-sm focus:ring-gray-500`
      case 'prominent':
        return `${base} bg-blue-600 text-white text-base shadow-lg focus:ring-blue-500 focus:bg-blue-700`
      default:
        return `${base} bg-gray-900 text-white text-sm focus:ring-gray-400 focus:bg-gray-800`
    }
  }

  return (
    <>
      {/* Skip Navigation Links - Only visible when focused */}
      <div className={`fixed top-4 ${getPositionClasses()} z-[9999]`}>
        <nav aria-label="Skip navigation links" role="navigation">
          <ul className="flex flex-col space-y-2">
            {allLinks.map((link, index) => (
              <li key={index}>
                <a
                  href={link.href}
                  onClick={(e) => handleSkipClick(e, link.href)}
                  className={`
                    ${getVariantClasses()}
                    absolute transform -translate-y-full opacity-0 pointer-events-none
                    focus:relative focus:transform-none focus:opacity-100 focus:pointer-events-auto
                    hover:shadow-lg active:scale-95
                  `}
                  tabIndex={0}
                >
                  {link.label}
                  <span className="sr-only"> (Skip link)</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Screen reader instructions */}
      <div className="sr-only">
        <p>Skip navigation links are available. Press Tab to access them, then Enter to skip to the selected section.</p>
      </div>
    </>
  )
}

/**
 * Hook for managing skip navigation targets
 */
export const useSkipNavigationTargets = () => {
  const registerTarget = (id: string, element: HTMLElement | null) => {
    if (element && !element.id) {
      element.id = id
    }
    
    // Ensure the element is accessible
    if (element && !element.getAttribute('tabindex') && !element.matches('a, button, input, textarea, select')) {
      element.setAttribute('tabindex', '-1')
    }
  }

  const ensureTargetAccessibility = (targetId: string) => {
    const target = document.getElementById(targetId)
    if (target) {
      // Add landmark role if not present
      if (!target.getAttribute('role') && !target.matches('main, nav, footer, aside, section, header')) {
        if (targetId.includes('main')) {
          target.setAttribute('role', 'main')
        } else if (targetId.includes('nav')) {
          target.setAttribute('role', 'navigation')
        } else if (targetId.includes('footer')) {
          target.setAttribute('role', 'contentinfo')
        } else {
          target.setAttribute('role', 'region')
        }
      }

      // Add aria-label if not present and no visible heading
      if (!target.getAttribute('aria-label') && !target.getAttribute('aria-labelledby')) {
        const heading = target.querySelector('h1, h2, h3, h4, h5, h6')
        if (!heading) {
          const labelText = targetId.replace(/[_-]/g, ' ').replace(/([A-Z])/g, ' $1').toLowerCase()
          target.setAttribute('aria-label', labelText.charAt(0).toUpperCase() + labelText.slice(1))
        }
      }
    }
  }

  return {
    registerTarget,
    ensureTargetAccessibility
  }
}

/**
 * Higher-order component to add skip navigation target
 */
export const withSkipNavigationTarget = <P extends { id?: string }>(
  WrappedComponent: React.ComponentType<P>,
  targetId: string
) => {
  const WithSkipTarget = React.forwardRef<HTMLElement, P>((props, ref) => {
    const { registerTarget } = useSkipNavigationTargets()
    const elementRef = React.useRef<HTMLElement>(null)

    React.useEffect(() => {
      registerTarget(targetId, elementRef.current)
    }, [])

    return (
      <WrappedComponent
        {...(props as any)}
        ref={ref || elementRef}
        id={targetId}
      />
    )
  })

  WithSkipTarget.displayName = `withSkipNavigationTarget(${WrappedComponent.displayName || WrappedComponent.name})`
  return WithSkipTarget
}

export default SkipNavigation